import React, { useState, useCallback } from 'react';
import { Search, Loader2, AlertCircle, Info } from 'lucide-react';
import { searchPacraCompany, searchZraTpin } from '../services/api';
import './CompanySearchPage.css';

function CompanySearchPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [pacraResults, setPacraResults] = useState([]);
    const [pacraLoading, setPacraLoading] = useState(false);
    const [pacraError, setPacraError] = useState(null);

    const [tpinDetails, setTpinDetails] = useState(null);
    const [tpinLoading, setTpinLoading] = useState(false);
    const [tpinError, setTpinError] = useState(null);
    const [hoveredEntityId, setHoveredEntityId] = useState(null);

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            setPacraError("Please enter a company name to search.");
            setPacraResults([]);
            return;
        }
        setPacraLoading(true);
        setPacraError(null);
        setPacraResults([]);
        setTpinDetails(null);

        try {
            // Call your backend proxy for PACRA using the API service
            const response = await searchPacraCompany(searchTerm);
            const data = response.data;

            setPacraResults(data || []);
            if (!data || data.length === 0) {
                setPacraError("No results found for your search term.");
            }
        } catch (error) {
            console.error("Error fetching PACRA data via proxy:", error);
            // Handle Axios error structure or generic error
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch company data.";
            setPacraError(`Failed to fetch company data: ${errorMessage}`);
        } finally {
            setPacraLoading(false);
        }
    };

    const fetchTpinDetails = useCallback(async (entityNumber, entityID) => {
        if (!entityNumber) return;
        setHoveredEntityId(entityID);
        setTpinLoading(true);
        setTpinError(null);
        setTpinDetails(null);

        try {
            // Call your backend proxy for ZRA using the API service
            const response = await searchZraTpin(entityNumber);
            const data = response.data;

            if (data && data.status === "FOUND" && data.message && data.message.length > 0) {
                setTpinDetails(data.message[0]);
            } else if (data && data.message) { // Handle cases where ZRA might return a message even if not "FOUND"
                setTpinError(data.message || "TPIN not found or invalid BRN.");
                setTpinDetails(null);
            }
            else {
                setTpinError("TPIN not found or invalid BRN.");
                setTpinDetails(null);
            }
        } catch (error) {
            console.error("Error fetching ZRA TPIN data via proxy:", error);
            // Handle Axios error structure or generic error
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch TPIN.";
            setTpinError(`Failed to fetch TPIN: ${errorMessage}`);
        } finally {
            setTpinLoading(false);
        }
    }, []);

    const handleMouseLeave = () => {
        setHoveredEntityId(null);
        // setTpinDetails(null); // Keep details if you want them to persist a bit
        // setTpinLoading(false);
        // setTpinError(null);
    };

    return (
        <div className="company-search-container">
            <h2>Search Company Register (PACRA)</h2>
            <div className="search-bar">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Enter company name..."
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button onClick={handleSearch} disabled={pacraLoading}>
                    {pacraLoading ? <Loader2 className="spinner" size={18} /> : <Search size={18} />}
                    Search
                </button>
            </div>

            {pacraError && <p className="error-message"><AlertCircle size={16} /> {pacraError}</p>}

            {pacraResults.length > 0 && (
                <div className="results-container">
                    <h3>PACRA Results:</h3>
                    <ul>
                        {pacraResults.map((company) => (
                            <li
                                key={company.entityID}
                                onMouseEnter={() => fetchTpinDetails(company.entityNumber, company.entityID)}
                                onMouseLeave={handleMouseLeave}
                                className="result-item"
                            >
                                <div className="company-info">
                                    <strong>{company.entityName}</strong> ({company.entityNumber})
                                    <br />
                                    Status: {company.entityStatus} | Type: {company.companyType}
                                    <br />
                                    Registered: {company.registrationDate}
                                </div>
                                {hoveredEntityId === company.entityID && (
                                    <div className="tpin-tooltip">
                                        {tpinLoading && <><Loader2 className="spinner-inline" size={14} /> Loading TPIN...</>}
                                        {tpinError && <span className="tpin-error"><AlertCircle size={14} /> {tpinError}</span>}
                                        {tpinDetails && !tpinLoading && !tpinError && (
                                            <>
                                                <Info size={14} /> <strong>ZRA TPIN:</strong> {tpinDetails.tpin}
                                                <br />
                                                Registered Name: {tpinDetails.taxPayerName}
                                            </>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default CompanySearchPage;